import { useCallback, useEffect, useRef, useState } from "react";
import { CircleCheck, TriangleAlert, Loader2, ClipboardCheck, Gauge, Camera, CircleDashed } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMessage, errFields, focusFirstError } from "@/lib/api";
import { initInspection, toPayload, validateInspection, viewsRequired } from "@/lib/inspection";
import { Section, FieldError } from "@/components/common";
import { PhotoInput } from "@/components/Photo";

const ItemRow = ({ doc, item, label, idx, error, onChange }) => {
  const stateBtn = (val, text, Icon, on) => (
    <button type="button" data-testid={`item-${idx}-${val}`} aria-pressed={item.state === val} onClick={() => onChange({ ...item, state: val })}
      className={`inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-md border-2 px-3 font-bold sm:flex-none ${item.state === val ? on : "border-border text-slate-300 hover:border-slate-500"}`}>
      <Icon className="h-5 w-5" /> {text}
    </button>
  );
  return (
    <li data-field={`items.${item.item_id}`} className={`rounded-lg border p-3 sm:p-4 ${error ? "border-red-500/70 bg-red-950/20" : "border-border bg-background/40"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-medium"><span className="mr-2 font-data text-amber-400">{idx + 1}.</span>{label}
          {!item.state && <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><CircleDashed className="h-3.5 w-3.5" /> Non renseigné</span>}</p>
        <div className="flex gap-2">
          {stateBtn("conforme", "Conforme", CircleCheck, "border-emerald-400 bg-emerald-950/70 text-emerald-200")}
          {stateBtn("anomalie", "Anomalie", TriangleAlert, "border-amber-400 bg-amber-950/70 text-amber-100")}
        </div>
      </div>
      {item.state === "anomalie" && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <Textarea data-testid={`item-${idx}-comment`} rows={2} value={item.comment} onChange={(e) => onChange({ ...item, comment: e.target.value })} placeholder="Décrivez l'anomalie constatée" className="text-base" />
          <div className="grid grid-cols-2 gap-2">
            {[["mineure", "Mineure"], ["critique", "Critique (bloque l'équipement)"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`item-${idx}-crit-${v}`} aria-pressed={item.criticality === v} onClick={() => onChange({ ...item, criticality: v })}
                className={`min-h-[48px] rounded-md border-2 px-2 text-sm font-bold ${item.criticality === v ? (v === "critique" ? "border-red-500 bg-red-950/80 text-red-100" : "border-amber-400 bg-amber-950/70 text-amber-100") : "border-border text-slate-300"}`}>{l}</button>
            ))}
          </div>
          <PhotoInput linkedType="inspection" linkedId={doc.id} value={item.photo_id} label="Photo de l'anomalie" testId={`item-${idx}-photo`} onChange={(fid) => onChange({ ...item, photo_id: fid })} />
        </div>
      )}
      <FieldError error={error} testId={`item-${idx}-error`} />
    </li>
  );
};

const Meter = ({ doc, d, set, errors }) => {
  const v = d.meter_value === "" ? null : Number(d.meter_value);
  const lower = doc.previous_meter_value != null && v !== null && v < doc.previous_meter_value;
  return (
    <Section title="Compteur" icon={Gauge} testId="inspection-meter-section" field="meter_value" error={errors.meter_value || errors.meter_photo}>
      <p className="text-sm text-muted-foreground">Relevé précédent : <span data-testid="meter-previous" className="font-data text-foreground">{doc.previous_meter_value ?? "aucun"}</span> {doc.meter_unit}</p>
      <label htmlFor="meter" className="mt-3 block text-sm font-semibold">Valeur relevée ({doc.meter_unit || "unité"})</label>
      <Input id="meter" data-testid="meter-value-input" type="number" inputMode="decimal" min="0" step="any" value={d.meter_value} onChange={(e) => set({ meter_value: e.target.value }, "meter_value")} className="mt-1.5 h-12 font-data text-base" />
      {lower && (
        <div className="mt-3">
          <p className="text-sm text-amber-200">La valeur est inférieure au relevé précédent. Corrigez-la ou justifiez l'écart :</p>
          <Textarea data-testid="meter-justification-input" rows={2} value={d.meter_justification} onChange={(e) => set({ meter_justification: e.target.value }, "meter_value")} className="mt-1.5 text-base" placeholder="Ex. compteur remplacé le…" />
        </div>
      )}
      <FieldError error={errors.meter_value} testId="meter-value-error" />
      <div className="mt-4" data-field="meter_photo">
        <PhotoInput linkedType="inspection" linkedId={doc.id} value={d.meter_photo_id} label="Photo du compteur (obligatoire)" testId="meter-photo" error={errors.meter_photo}
          onChange={(fid) => set({ meter_photo_id: fid }, "meter_photo")} />
        <FieldError error={errors.meter_photo} testId="meter-photo-error" />
      </div>
    </Section>
  );
};

export const InspectionForm = ({ doc, onDone }) => {
  const [d, setD] = useState(() => initInspection(doc));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const first = useRef(true);
  const done = useRef(false);

  const set = (patch, errKey) => {
    setD((p) => ({ ...p, ...patch }));
    if (errKey) setErrors((p) => { const n = { ...p }; delete n[errKey]; return n; });
  };
  const save = useCallback((data) => { if (!done.current) api.put(`/inspections/${doc.id}`, toPayload(data)).catch(() => {}); }, [doc.id]);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(d), 1000);
    return () => clearTimeout(timer.current);
  }, [d, save]);

  const fail = (errs, msg) => { setErrors(errs); focusFirstError(errs); toast.error(msg || `Inspection non finalisée : ${Object.keys(errs).length} point(s) à compléter.`); };
  const finalize = async () => {
    if (busy) return;
    clearTimeout(timer.current);
    const errs = validateInspection(doc, d);
    if (Object.keys(errs).length) { save(d); return fail(errs); }
    setBusy(true);
    try {
      const r = await api.post(`/inspections/${doc.id}/finalize`, toPayload(d));
      done.current = true;
      toast.success(r.data.result === "non_conforme" ? "Inspection finalisée : équipement BLOQUÉ (anomalie critique)." : "Inspection finalisée.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDone(r.data);
    } catch (e) { fail(errFields(e), errMessage(e)); } finally { setBusy(false); }
  };

  const required = viewsRequired(doc, d);
  const labels = Object.fromEntries(doc.items.map((i) => [i.item_id, i.label]));
  return (
    <div className="space-y-5">
      <Section title="Points de contrôle" icon={ClipboardCheck} testId="inspection-items-section">
        <ol className="space-y-3">
          {d.items.map((it, idx) => (
            <ItemRow key={it.item_id} doc={doc} item={it} label={labels[it.item_id]} idx={idx} error={errors[`items.${it.item_id}`]}
              onChange={(ni) => set({ items: d.items.map((x) => (x.item_id === it.item_id ? ni : x)) }, `items.${it.item_id}`)} />
          ))}
        </ol>
      </Section>
      {doc.has_meter && <Meter doc={doc} d={d} set={set} errors={errors} />}
      {doc.photo_views.length > 0 && (
        <Section title="Photos de prise en charge" icon={Camera} testId="inspection-views-section">
          <p data-testid="views-required-info" className="mb-3 text-sm text-muted-foreground">{required ? "Toutes les vues ci-dessous sont obligatoires." : "Photos facultatives pour cette prise en charge (deviennent obligatoires en cas d'anomalie)."}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {doc.photo_views.map((v, idx) => (
              <div key={v.id} data-field={`views.${v.id}`}>
                <PhotoInput linkedType="inspection" linkedId={doc.id} value={d.view_photos[v.id]} label={`Vue : ${v.label}`} testId={`view-${idx}-photo`} error={errors[`views.${v.id}`]}
                  onChange={(fid) => set({ view_photos: { ...d.view_photos, [v.id]: fid } }, `views.${v.id}`)} />
                <FieldError error={errors[`views.${v.id}`]} testId={`view-${idx}-error`} />
              </div>
            ))}
          </div>
        </Section>
      )}
      <div className="sticky bottom-[72px] z-20 rounded-xl border border-border bg-card/95 p-3 backdrop-blur-xl sm:p-4 md:bottom-4">
        {Object.keys(errors).length > 0 && <p data-testid="inspection-error-count" role="alert" className="mb-2 text-sm font-semibold text-red-300">{Object.keys(errors).length} point(s) à compléter</p>}
        <FieldError error={errors.photos} testId="inspection-photos-error" />
        <Button data-testid="finalize-inspection-button" onClick={finalize} disabled={busy} className="h-14 w-full text-base font-bold">
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ClipboardCheck className="mr-2 h-5 w-5" />} Finaliser l'inspection
        </Button>
      </div>
    </div>
  );
};
